// Abgleich mit der API: Änderungen bleiben lokal, bis der Nutzer „Speichern“ wählt (oder Strg+S), und gehen dann
// als Paket an den Server – je Element mit der Zeilenversion, die der Client zuletzt vom Server kannte. Meldet der Server einen
// Konflikt (409), werden die betroffenen Elemente feldweise zusammengeführt und erneut gesendet.
// „Verwerfen“ setzt auf den zuletzt gespeicherten Stand zurück.
// Fremde Änderungen werden alle 10 s nachgeladen und ebenso eingearbeitet.
import { api, ApiFehler, type Aenderung, type Archiv, type Daten, type Rolle, type Zeilen } from './api';
import { getState, load, markSaved, replaceData } from './logik/store';
import { merge3, same } from './logik/merge.js';
import { migrate } from './logik/migrate.js';

const SAMMLUNGEN = ['lanes', 'systems', 'edgeTypes', 'processes', 'decisions', 'nodes', 'edges'] as const;
const META_FELDER = ['titel', 'untertitel', 'beschreibung', 'erstelltVon'];
const VERSION_FELDER = ['version', 'stand'];
const POLL_MS = 10_000;
const VERSUCHE = 3;

export type Status = 'laedt' | 'aktuell' | 'speichert' | 'fehler';

export const sync = {
  projektId: '',
  rolle: 'lesend' as Rolle,
  /** Archiviert: nur lesbar, bis ein Admin es wiederherstellt. */
  archiv: null as Archiv | null,
  status: 'laedt' as Status,
  fehler: '',
  savedAt: null as Date | null,
  nr: 0,
};
let base: Daten = null; // Stand, den der Server zuletzt bestätigt hat
let zeilen: Zeilen = {};

const listeners = new Set<() => void>();
export const onSync = (fn: () => void) => (listeners.add(fn), () => void listeners.delete(fn));
const set = (patch: Partial<typeof sync>) => {
  Object.assign(sync, patch);
  for (const l of listeners) l();
};

/** Hinweise an die Oberfläche (Toast), wird von der App gesetzt. */
export const meldungen = { zeigen: (_text: string, _fehler = false) => {} };

const ROLLEN: Rolle[] = ['lesend', 'mitwirkend', 'verantwortlich', 'admin'];
/** Darf die Person das (mindestens diese Rolle)? Archivierte Projekte sind für alle nur lesbar. */
export const darf = (mindestens: Rolle) =>
  (!sync.archiv || mindestens === 'lesend') && ROLLEN.indexOf(sync.rolle) >= ROLLEN.indexOf(mindestens);
/** Wie darf(), Superadmins dürfen immer – außer in archivierten Projekten. */
export const darfOderSa = (mindestens: Rolle, superadmin?: boolean) => darf(mindestens) || (!!superadmin && !sync.archiv);
/** Archivstand nach Archivieren/Wiederherstellen übernehmen. */
export const archivSetzen = (archiv: Archiv | null) => set({ archiv });

export async function projektOeffnen(id: string) {
  projektSchliessen();
  set({ projektId: id, status: 'laedt', fehler: '' });
  try {
    const r = await api.projekt(id);
    const daten = migrate(r.daten);
    base = structuredClone(daten);
    zeilen = r.zeilen;
    set({ rolle: r.rolle, archiv: r.archiv ?? null, nr: r.nr, status: 'aktuell' });
    load(daten);
    poller = window.setInterval(nachladen, POLL_MS);
  } catch (e) {
    set({ status: 'fehler', fehler: (e as Error).message });
    throw e;
  }
}

export function projektSchliessen() {
  if (poller) window.clearInterval(poller);
  poller = 0;
  base = null;
  zeilen = {};
  set({ projektId: '', nr: 0, archiv: null });
}

// ---------- Unterschied je Element ----------
const nachId = (liste: Daten[] = []) => new Map<string, Daten>(liste.map((x) => [x.id, x]));
const zeileVon = (sammlung: string, id: string) => (zeilen[sammlung] as Record<string, string> | undefined)?.[id] ?? null;

export function unterschied(alt: Daten, neu: Daten): Aenderung[] {
  const out: Aenderung[] = [];
  for (const s of SAMMLUNGEN) {
    const A = nachId(alt[s]);
    const N = nachId(neu[s]);
    for (const [id, el] of N) {
      const a = A.get(id);
      if (!a || !same(a, el)) out.push({ sammlung: s, id, op: 'put', element: el, zeile: a ? zeileVon(s, id) : null });
    }
    for (const id of A.keys()) if (!N.has(id)) out.push({ sammlung: s, id, op: 'del', zeile: zeileVon(s, id) });
  }
  // Version und Stand führt der Server; sie gehen nur mit, wenn ein Admin sie hier ausdrücklich geändert hat
  const versionFelder = VERSION_FELDER.filter((f) => !same(alt.meta?.[f], neu.meta?.[f]));
  if (versionFelder.length || META_FELDER.some((f) => !same(alt.meta?.[f], neu.meta?.[f])))
    out.push({
      sammlung: 'meta', id: sync.projektId, op: 'put', zeile: zeilen.meta as string,
      element: Object.fromEntries([...META_FELDER, ...versionFelder].map((f) => [f, neu.meta?.[f] ?? null])),
    });
  return out;
}

/** Bestätigte Änderungen in die Basis übernehmen. */
function inBasis(pakete: Aenderung[], neueZeilen: Zeilen) {
  const b = structuredClone(base);
  for (const p of pakete) {
    if (p.sammlung === 'meta') { Object.assign(b.meta, p.element); continue; }
    const liste: Daten[] = b[p.sammlung];
    const i = liste.findIndex((x) => x.id === p.id);
    if (p.op === 'del') { if (i >= 0) liste.splice(i, 1); }
    else if (i >= 0) liste[i] = structuredClone(p.element);
    else liste.push(structuredClone(p.element));
  }
  base = b;
  for (const [s, z] of Object.entries(neueZeilen ?? {})) {
    if (typeof z === 'string') zeilen[s] = z;
    else zeilen[s] = { ...((zeilen[s] as Record<string, string>) ?? {}), ...z };
  }
  for (const p of pakete) if (p.op === 'del') delete (zeilen[p.sammlung] as Record<string, string>)?.[p.id];
}

/** Fremden Stand (theirs) mit den eigenen offenen Änderungen zusammenführen. */
function einarbeiten(theirs: Daten, hinweis: string) {
  const mine = getState().data;
  const offen = getState().dirty;
  const { data, conflicts } = merge3(base, mine, theirs);
  base = theirs;
  const nochOffen = offen && unterschied(base, data).length > 0;
  replaceData(nochOffen ? data : theirs, { dirty: nochOffen, resetHistory: true });
  if (conflicts.length) meldungen.zeigen(`${hinweis} Bei gleichzeitig geänderten Feldern gilt Ihre Fassung: ${conflicts.slice(0, 3).join('; ')}`, true);
}

/** Liegt das Element schon auf dem Server? Kommentare, Aufgaben und Anhänge brauchen einen gespeicherten Bezug. */
export function aufServer(sammlung: (typeof SAMMLUNGEN)[number], id: string) {
  return !!base?.[sammlung]?.some((x: Daten) => x.id === id);
}

/** Ungespeicherte Änderungen verwerfen: zurück zum zuletzt gespeicherten Stand. */
export function verwerfen() {
  if (!base || speichernLaeuft) return;
  replaceData(structuredClone(base), { dirty: false, resetHistory: true });
  set({ status: 'aktuell', fehler: '' });
}

// ---------- Speichern ----------
let speichernLaeuft: Promise<void> | null = null;
let nochmal = false;

export function jetztSpeichern(): Promise<void> {
  if (speichernLaeuft) { nochmal = true; return speichernLaeuft; }
  speichernLaeuft = speichern().finally(() => {
    speichernLaeuft = null;
    if (nochmal) { nochmal = false; void jetztSpeichern(); }
  });
  return speichernLaeuft;
}

async function speichern() {
  if (!sync.projektId || !base || !getState().dirty) return;
  set({ status: 'speichert' });
  for (let versuch = 0; versuch < VERSUCHE; versuch++) {
    const mine = getState().data;
    const pakete = unterschied(base, mine);
    if (pakete.length === 0) { markSaved(); set({ status: 'aktuell' }); return; }
    try {
      const r = await api.speichern(sync.projektId, pakete);
      inBasis(pakete, r.zeilen);
      if (getState().data === mine) markSaved();
      set({ status: 'aktuell', savedAt: new Date(), fehler: '' });
      return;
    } catch (e) {
      if (e instanceof ApiFehler && e.status === 409) {
        konflikteUebernehmen((e.daten as { konflikte: Record<string, Record<string, { element: Daten; zeile: string }>> }).konflikte);
        continue;
      }
      set({ status: 'fehler', fehler: (e as Error).message });
      meldungen.zeigen('Speichern fehlgeschlagen: ' + (e as Error).message, true);
      return;
    }
  }
  set({ status: 'fehler', fehler: 'Zu viele gleichzeitige Änderungen – bitte erneut speichern.' });
}

/** Vom Server gemeldete aktuelle Elemente als fremden Stand einarbeiten. */
function konflikteUebernehmen(konflikte: Record<string, Record<string, { element: Daten; zeile: string }>>) {
  const theirs = structuredClone(base);
  for (const [s, eintraege] of Object.entries(konflikte)) {
    for (const [id, { element, zeile }] of Object.entries(eintraege)) {
      if (s === 'meta') { Object.assign(theirs.meta, element); zeilen.meta = zeile; continue; }
      const liste: Daten[] = theirs[s];
      const i = liste.findIndex((x) => x.id === id);
      if (element) { if (i >= 0) liste[i] = element; else liste.push(element); }
      else if (i >= 0) liste.splice(i, 1);
      const z = ((zeilen[s] as Record<string, string>) ??= {});
      if (zeile) z[id] = zeile; else delete z[id];
    }
  }
  einarbeiten(migrate(theirs), 'Zusammengeführt mit Änderungen anderer.');
}

// ---------- Nachladen ----------
let poller = 0;

/** Sofort nachladen, z. B. nachdem der Server selbst Elemente angelegt hat (Besprechungsimport → Entscheidungen). */
export const nachladenJetzt = () => nachladen();

async function nachladen() {
  if (!sync.projektId || speichernLaeuft || !base) return;
  try {
    const r = await api.seit(sync.projektId, sync.nr);
    if (r.nr === sync.nr || speichernLaeuft) return;
    const theirs = structuredClone(base);
    for (const [s, liste] of Object.entries(r.elemente ?? {}))
      for (const el of liste) {
        const i = theirs[s].findIndex((x: Daten) => x.id === el.id);
        if (i >= 0) theirs[s][i] = el; else theirs[s].push(el);
      }
    for (const [s, ids] of Object.entries(r.geloescht ?? {}))
      theirs[s] = theirs[s].filter((x: Daten) => !ids.includes(x.id));
    if (r.meta) theirs.meta = r.meta;
    if (r.history) theirs.history = r.history;
    for (const [s, z] of Object.entries(r.zeilen ?? {}))
      zeilen[s] = typeof z === 'string' ? z : { ...((zeilen[s] as Record<string, string>) ?? {}), ...z };
    set({ nr: r.nr });
    einarbeiten(migrate(theirs), 'Änderungen anderer übernommen.');
  } catch {
    /* Netz kurz weg – beim nächsten Mal wieder */
  }
}
